import * as genericService from "./genericFormServices";
import type { LegalAdviceFormEightAData } from "../types/LegalAdvices/LegalAdviceFormEightA.type";
import { formatSignatureUrl } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";

const ENDPOINT = "legal-advice-form-eight-a";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormEightAData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Eight
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormEightAData => {
  if (!item) return {} as LegalAdviceFormEightAData;

  // Extract inner form values safely
  const form =
    item.legalAdviceFormEightA ||
    item.legalAdviceForm ||
    item.formData ||
    {};

  return {
    // Document Level Identifiers
    id: item.id || form.documentId || form.id || '',
    documentNumber: item.documentNumber || '',
    formType: item.formType || 'LEGAL_ADVICE_FORM_EIGHT_A',
    status: item.status || form.status || 'DRAFT',
    rejectionReason: item.rejectionReason || form.rejectionReason || '',

    // Geo Levels & Attachments
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],
    attachments: item.attachments || form.attachments || [],

    // 2. Legal Advice Specific Form Fields
    office: form.office || '',

    // အပိုင်း ၁။ အချက်အလက်များ (၁ မှ ၉)
    scrutinyCaseNo: form.scrutinyCaseNo || '',
    appealRegisterNo: form.appealRegisterNo || form.scrutinyRegisterNo || '',
    submittingOfficeName: form.submittingOfficeName || form.submitterNameOrOffice || '',
    highCourtDetails: form.highCourtDetails || form.acquittalCourtDetails || '',
    originalCourtDetails: form.originalCourtDetails || '',
    chargedSection: form.chargedSection || form.chargedLawSection || form.prosecutedLawSection || '',
    originalJudgmentDate: form.originalJudgmentDate || '',
    appealCourtDetails: form.appealCourtDetails || '',
    appealDeadlineDate: form.appealDeadlineDate || form.limitationExpiryDate || '',

    // အပိုင်း ၂။ သုံးသပ်ချက်များ (၁၀)
    initialOfficerOpinionProsecution:
      form.initialOfficerOpinionProsecution || form.plaintiffArgumentAnalysis || '',
    initialOfficerOpinionDefense:
      form.initialOfficerOpinionDefense || form.defendantArgumentAnalysis || '',
    jointOfficerOpinionFitness:
      form.jointOfficerOpinionFitness || form.courtJudgmentAnalysis || '',

    // ကနဦး စိစစ်သည့် ဥပဒေအရာရှိ လက်မှတ်
    initialOfficerSignName: form.initialOfficerSignName || form.handlingOfficerName || '',
    initialOfficerRank: form.initialOfficerRank || '',
    initialOfficerReceiveDate: form.initialOfficerReceiveDate || '',
    initialOfficerSubmitDate: form.initialOfficerSubmitDate || form.handlingOfficerSubmitDate || '',
    initialOfficerSignatureImage: formatSignatureUrl(
      form.initialOfficerSignatureImage || form.handlingOfficerSignature
    ),

    // ညွှန်ကြားရေးမှူး သဘောထားမှတ်ချက်
    directorOpinion: form.directorOpinion || form.inChargeOfficerOpinion || '',
    directorSignName: form.directorSignName || form.inChargeOfficerName || '',
    directorReceiveDate: form.directorReceiveDate || '',
    directorSubmitDate: form.directorSubmitDate || form.inChargeOfficerSubmitDate || '',
    directorSignatureImage: formatSignatureUrl(
      form.directorSignatureImage || form.inChargeOfficerSignature
    ),

    // ဒုတိယညွှန်ကြားရေးမှူးချုပ် သဘောထားမှတ်ချက်
    deputyDirectorGeneralOpinion: form.deputyDirectorGeneralOpinion || '',
    deputyDirectorGeneralSignName: form.deputyDirectorGeneralSignName || '',
    deputyDirectorGeneralReceiveDate: form.deputyDirectorGeneralReceiveDate || '',
    deputyDirectorGeneralSubmitDate: form.deputyDirectorGeneralSubmitDate || '',
    deputyDirectorGeneralSignatureImage: formatSignatureUrl(
      form.deputyDirectorGeneralSignatureImage
    ),

    // ညွှန်ကြားရေးမှူးချုပ် သဘောထားမှတ်ချက်
    directorGeneralOpinion: form.directorGeneralOpinion || '',
    directorGeneralSignName: form.directorGeneralSignName || '',
    directorGeneralReceiveDate: form.directorGeneralReceiveDate || '',
    directorGeneralSubmitDate: form.directorGeneralSubmitDate || '',
    directorGeneralSignatureImage: formatSignatureUrl(
      form.directorGeneralSignatureImage
    ),

    // ဒုတိယရှေ့နေချုပ် ဆုံးဖြတ်ချက်
    deputyAttorneyGeneralOpinion: form.deputyAttorneyGeneralOpinion || '',
    deputyAttorneyGeneralSignName: form.deputyAttorneyGeneralSignName || '',
    deputyAttorneyGeneralSubmitDate: form.deputyAttorneyGeneralSubmitDate || '',
    deputyAttorneyGeneralSignatureImage: formatSignatureUrl(
      form.deputyAttorneyGeneralSignatureImage
    ),

    // ပြည်ထောင်စုရှေ့နေချုပ် ဆုံးဖြတ်ချက်
    unionAttorneyGeneralOpinion: form.unionAttorneyGeneralOpinion || '',
    unionAttorneyGeneralSignName: form.unionAttorneyGeneralSignName || '',
    unionAttorneyGeneralSubmitDate: form.unionAttorneyGeneralSubmitDate || '',
    unionAttorneyGeneralSignatureImage: formatSignatureUrl(
      form.unionAttorneyGeneralSignatureImage
    ),

    // Dates & Metadata
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory) ? item.stageHistory : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Eight
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormEightAData,
  params: FetchEightAParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;

  // Form Eight Date Matching
  const formattedAppDate = app.submitDate || app.createdAt || '';

  const matchesDate = !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  // Search matching across Form Eight fields
  const matchesGeneralSearch =
    !term ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term)) ||
    Boolean(app.scrutinyCaseNo && app.scrutinyCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.appealRegisterNo && app.appealRegisterNo.toLowerCase().includes(term)) ||
    Boolean(app.submittingOfficeName && app.submittingOfficeName.toLowerCase().includes(term)) ||
    Boolean(app.highCourtDetails && app.highCourtDetails.toLowerCase().includes(term)) ||
    Boolean(app.originalCourtDetails && app.originalCourtDetails.toLowerCase().includes(term)) ||
    Boolean(app.chargedSection && app.chargedSection.toLowerCase().includes(term)) ||
    Boolean(app.initialOfficerSignName && app.initialOfficerSignName.toLowerCase().includes(term)) ||
    Boolean(app.directorSignName && app.directorSignName.toLowerCase().includes(term)) ||
    Boolean(app.deputyDirectorGeneralSignName && app.deputyDirectorGeneralSignName.toLowerCase().includes(term)) ||
    Boolean(app.directorGeneralSignName && app.directorGeneralSignName.toLowerCase().includes(term)) ||
    Boolean(app.deputyAttorneyGeneralSignName && app.deputyAttorneyGeneralSignName.toLowerCase().includes(term)) ||
    Boolean(app.unionAttorneyGeneralSignName && app.unionAttorneyGeneralSignName.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export interface FetchEightAParams {
  searchTerm?: string;
  courtSearch?: string;
  partySearch?: string;
  selectedStatus?: string;
  selectedDate?: string;
  selectedTownship?:string;
  currentPage: number;
  itemsPerPage: number;
}



export const fetchLegalAdviceApplications = (
  params: FetchEightAParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormEightAData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormEightAData> =>
  genericService.fetchApplicationById<LegalAdviceFormEightAData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormEightAData>
) => genericService.saveDraft<LegalAdviceFormEightAData>(ENDPOINT, id, data);

export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormEightAData
) => genericService.submitForm<LegalAdviceFormEightAData>(ENDPOINT, id, data);

export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);